import { yupResolver } from '@hookform/resolvers/yup'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import omitBy from 'lodash/omitBy'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'

import Button from '@/components/Button'
import type { FormData } from '@/pages/getRecipe/shadeGuide/utils/formTypes'
import {
  FORM_FIELDS,
  validationSchema,
} from '@/pages/getRecipe/shadeGuide/utils/formTypes'
import type { RootState } from '@/store'
import {
  useGenerateShadeGuideLayeringRecipeMutation,
  useGenerateShadeGuideMonolithicRecipeMutation,
} from '@/store/apiService'
import {
  setRestorationMethodOnRecipeGenerate,
  setShadeGuideActiveStep,
  setShadeGuideRecipeOutput,
} from '@/store/shadeGuide'

import { RECIPE_TYPE, RESTORATION_METHOD } from '../utils/constants'
import Material from './Material'
import MeasureColor from './MeasureColor'

const ShadeGuidePanel = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'buttons',
  })

  const navigate = useNavigate()
  const formValues = useSelector((state: RootState) => state.shadeGuide.form)
  const shadeGuideActiveStep = useSelector(
    (state: RootState) => state.shadeGuide.shadeGuideActiveStep,
  )

  const canvasImage = useSelector((state: RootState) => state.app.canvasImage)

  const dispatch = useDispatch()

  const [
    generateShadeGuideMonolithicRecipe,
    // { isLoading: isGeneratingMonolithicRecipe },
  ] = useGenerateShadeGuideMonolithicRecipeMutation()
  const [
    generateShadeGuideLayeringRecipe,
    // { isLoading: isGeneratingLayeringRecipe },
  ] = useGenerateShadeGuideLayeringRecipeMutation()

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
    setValue,
    clearErrors,
  } = useForm<FormData>({
    defaultValues: formValues || {},
    resolver: yupResolver(validationSchema()),
    mode: 'onChange',
  })

  const recipeTypeValue = watch(FORM_FIELDS.RECIPE_TYPE) || ''
  const restorationMethodValue = watch(FORM_FIELDS.RESTORATION_METHOD) || ''
  const onNext = useCallback(async () => {
    let isValid = false
    switch (shadeGuideActiveStep) {
      case 0:
        const colorFieldsToValidate: (keyof FormData)[] = [
          FORM_FIELDS.RECIPE_TYPE,
          FORM_FIELDS.RESTORATION_METHOD,
          FORM_FIELDS.STUMP_SHADE,
          FORM_FIELDS.TARGET_COLOR,
          FORM_FIELDS.TARGET_COLOR_CHROMA_MIDDLE,
          FORM_FIELDS.TARGET_COLOR_VALUE_MIDDLE,
        ]

        if (recipeTypeValue === RECIPE_TYPE.TRIPLE_ZONE) {
          colorFieldsToValidate.push(
            FORM_FIELDS.TARGET_COLOR_CHROMA_CERVICAL,
            FORM_FIELDS.TARGET_COLOR_CHROMA_INCISAL,
            FORM_FIELDS.TARGET_COLOR_VALUE_CERVICAL,
            FORM_FIELDS.TARGET_COLOR_VALUE_INCISAL,
          )
        }

        isValid = await trigger(colorFieldsToValidate)
        break
      case 1:
        const fieldsToValidate: (keyof FormData)[] = [
          FORM_FIELDS.BRAND,
          FORM_FIELDS.FRAMEWORK_COLOR,
          FORM_FIELDS.FRAMEWORK_OPTIONS,
          FORM_FIELDS.STAINING_BRAND,
          FORM_FIELDS.AVAILABLE_SPACE_MIDDLE,
        ]

        if (recipeTypeValue === RECIPE_TYPE.TRIPLE_ZONE) {
          fieldsToValidate.push(
            FORM_FIELDS.AVAILABLE_SPACE_CERVICAL,
            FORM_FIELDS.AVAILABLE_SPACE_INCISAL,
          )
        }

        isValid = await trigger(fieldsToValidate)
        break
      default:
        isValid = false
    }

    if (isValid) {
      dispatch(setShadeGuideActiveStep(shadeGuideActiveStep + 1))
    }
  }, [
    dispatch,
    shadeGuideActiveStep,
    trigger,
    watch,
    canvasImage,
    recipeTypeValue,
  ])

  const onBack = useCallback(() => {
    dispatch(setShadeGuideActiveStep(shadeGuideActiveStep - 1))
  }, [dispatch, shadeGuideActiveStep])

  const onSubmit = useCallback((data: FormData) => {
    try {
      const {
        [FORM_FIELDS.TARGET_COLOR_VALUE_MIDDLE]: targetcolor_value_middle,
        [FORM_FIELDS.TARGET_COLOR_VALUE_CERVICAL]: targetcolor_value_cervical,
        [FORM_FIELDS.TARGET_COLOR_VALUE_INCISAL]: targetcolor_value_incisal,
        [FORM_FIELDS.TARGET_COLOR_CHROMA_MIDDLE]: targetcolor_chroma_middle,
        [FORM_FIELDS.TARGET_COLOR_CHROMA_CERVICAL]: targetcolor_chroma_cervical,
        [FORM_FIELDS.TARGET_COLOR_CHROMA_INCISAL]: targetcolor_chroma_incisal,
        [FORM_FIELDS.RESTORATION_METHOD]: restoration_method,
        [FORM_FIELDS.PREPARATION_COLOR]: prepcolor,
        [FORM_FIELDS.PREPARATION_COLOR_CERVICAL]: prepcolor_cervical,
        [FORM_FIELDS.PREPARATION_COLOR_INCISAL]: prepcolor_incisal,
        [FORM_FIELDS.TARGET_COLOR]: targetcolor,
        [FORM_FIELDS.AVAILABLE_SPACE]: available_space,
        [FORM_FIELDS.AVAILABLE_SPACE_CERVICAL]: availspace_cervical,
        [FORM_FIELDS.AVAILABLE_SPACE_INCISAL]: availspace_incisal,
        [FORM_FIELDS.AVAILABLE_SPACE_MIDDLE]: availspace_middle,
        [FORM_FIELDS.BRAND]: brand,

        ...rest
      } = data

      dispatch(setRestorationMethodOnRecipeGenerate(restoration_method))
      const payload = {
        ...rest,
        targetcolor_middle: [
          targetcolor_value_middle,
          targetcolor_chroma_middle,
        ],

        ...(data.recipe_type === RECIPE_TYPE.TRIPLE_ZONE && {
          targetcolor_cervical: [
            targetcolor_value_cervical,
            targetcolor_chroma_cervical,
          ],
          targetcolor_incisal: [
            targetcolor_value_incisal,
            targetcolor_chroma_incisal,
          ],
          prepcolor_cervical: prepcolor_cervical,
          prepcolor_incisal: prepcolor_incisal,
        }),

        ...(data.restoration_method === RESTORATION_METHOD.LAYERING && {
          brand,
          availspace_middle: `${Number(availspace_middle).toFixed(1)} mm`,
          ...(data.recipe_type === RECIPE_TYPE.TRIPLE_ZONE && {
            availspace_cervical: `${Number(availspace_cervical).toFixed(1)} mm`,
            availspace_incisal: `${Number(availspace_incisal).toFixed(1)} mm`,
          }),
        }),
      }
      // Handle form submission
      const updatedPayload = omitBy(payload, isEmpty)

      if (restoration_method === RESTORATION_METHOD.STAINING) {
        generateShadeGuideMonolithicRecipe(updatedPayload)
          .unwrap()
          .then((res) => {
            dispatch(setShadeGuideRecipeOutput(res))
            dispatch(setShadeGuideActiveStep(0))
            // dispatch(resetState())
            navigate('/shade-guide-ai-output')
          })
          .catch((e) => {
            toast.error(e?.data?.message || 'Error generating recipe')
          })
      } else {
        generateShadeGuideLayeringRecipe(updatedPayload)
          .unwrap()
          .then((res) => {
            dispatch(setShadeGuideRecipeOutput(res))
            dispatch(setShadeGuideActiveStep(0))
            // dispatch(resetState())
            navigate('/shade-guide-ai-output')
          })
          .catch((e) => {
            toast.error(e?.data?.message || 'Error generating recipe')
          })
      }
    } catch (error) {
      toast.error(error?.data?.message || 'Error generating recipe')
      console.error('Form submission error:', error)
    }
  }, [])

  const renderStepContent = useMemo(() => {
    switch (shadeGuideActiveStep) {
      case 0:
        return (
          <MeasureColor
            control={control}
            setValue={setValue}
            clearErrors={clearErrors}
          />
        )
      case 1:
        return (
          <Material
            control={control}
            recipeTypeValue={recipeTypeValue}
            restorationMethodValue={restorationMethodValue}
          />
        )

      default:
        return null
    }
  }, [shadeGuideActiveStep, control, recipeTypeValue, errors])

  const renderButtons = useMemo(
    () => (
      <Box px={2} py={5} display="flex" gap={2}>
        {shadeGuideActiveStep > 0 && (
          <Button
            label={t('back')}
            variant="outlined"
            onClick={onBack}
            startIcon={<ChevronLeftIcon />}
            textTransform="uppercase"
            sx={shadeGuideActiveStep === 1 ? {} : { flexGrow: 1 }}
          />
        )}
        {shadeGuideActiveStep < 1 ? (
          <Button
            label={t('next')}
            onClick={onNext}
            endIcon={<ChevronRightIcon />}
            textTransform="uppercase"
            sx={{ flexGrow: 1 }}
          />
        ) : (
          <Button
            label={t('generateRecipe')}
            onClick={handleSubmit(onSubmit)}
            textTransform="uppercase"
            sx={{ flexGrow: 2 }}
          />
        )}
      </Box>
    ),
    [shadeGuideActiveStep, onBack, onNext, handleSubmit, onSubmit],
  )

  return (
    <Box display="flex" flexDirection="column" flexGrow={1} overflow="auto">
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        gap={1}
        overflow="auto"
      >
        {renderStepContent}
      </Box>
      {renderButtons}
    </Box>
  )
}

export default ShadeGuidePanel
